// ─────────────────────────────────────────────────────────────────────────────
// LEVEL DATA  –  one handcrafted side-scrolling level
// World units = pixels at 1× scale.  The canvas scales to fit WORLD_HEIGHT.
// ─────────────────────────────────────────────────────────────────────────────

export const WORLD_WIDTH  = 14400;
export const WORLD_HEIGHT = 900;
export const GROUND_Y     = 820;   // y of the top of the main ground strip

// ─── Colour palette ──────────────────────────────────────────────────────────
export const NEON  = '#00ffe7';   // primary neon accent (cyan-green)
export const NEON2 = '#b44fff';   // secondary neon (violet) – skill labels etc.
export const DARK  = '#0a0a0f';
export const MID   = '#12121a';
export const DIM   = '#1e1e2e';

// ─── Platform definition ─────────────────────────────────────────────────────
export type Platform = {
  x: number;
  y: number;
  w: number;
  h: number;
  color?: string;
  label?: string;       // skill-label or zone marker
  tidbit?: string;      // toast body when landed on
  infoId?: string;      // unique id so toast shows once
  moving?: boolean;
  moveAxis?: 'x' | 'y';
  moveRange?: number;   // px either side of origin
  moveSpeed?: number;   // px per second
  hasJetpack?: boolean; // jetpack pickup lives on this platform
};

// ─── Spike / hazard ──────────────────────────────────────────────────────────
export type Spike = {
  x: number;
  y: number;
  w: number;
};

// ─── Enemy (patrol) ──────────────────────────────────────────────────────────
export type Enemy = {
  id: number;
  x: number;
  y: number;
  w: number;
  h: number;
  patrolLeft: number;
  patrolRight: number;
  speed: number;
  type?: 'ground' | 'flying';
  patrolTop?: number;
  patrolBottom?: number;
};

// ─── Toast / info card ───────────────────────────────────────────────────────
export type ZoneCard = {
  id: number;
  triggerX: number;   // player crosses this x → toast shown
  triggerW?: number;  // optional wider trigger band
  title: string;
  lines: string[];
  links?: { label: string; url: string }[];
};

// ─── Checkpoint ──────────────────────────────────────────────────────────────
export type Checkpoint = {
  x: number;
  zoneId: number;
};

// ─── NPC ─────────────────────────────────────────────────────────────────────
export type Npc = {
  x: number;
  y: number;
  w: number;
  h: number;
  interactR: number;  // radius for E prompt
  lines: { title: string; body: string[] }[];
};

// ═════════════════════════════════════════════════════════════════════════════
// PLATFORMS
// Zone 1  0–2400      Intro
// Zone 2  2400–5400   Skills
// Zone 3  5400–8200   Projects
// Zone 4  8200–11200  Experience
// Zone 5  11200–14400 Contact / End
// ═════════════════════════════════════════════════════════════════════════════
export const PLATFORMS: Platform[] = [
  // ── Ground (segmented so gaps exist) ───────────────────────────────────────
  // Gap under first floating platform (~360–620)
  { x: -200,  y: GROUND_Y, w: 560,  h: 80, color: '#1a1a2e' },
  { x: 620,   y: GROUND_Y, w: 1180, h: 80, color: '#1a1a2e' },
  { x: 2050,  y: GROUND_Y, w: 500,  h: 80, color: '#1a1a2e' },
  { x: 2800,  y: GROUND_Y, w: 1400, h: 80, color: '#1a1a2e' },
  { x: 4200,  y: GROUND_Y, w: 200,  h: 80, color: '#1a1a2e' },
  { x: 4600,  y: GROUND_Y, w: 200,  h: 80, color: '#1a1a2e' },
  { x: 5000,  y: GROUND_Y, w: 800,  h: 80, color: '#1a1a2e' },
  { x: 5800,  y: GROUND_Y, w: 2600, h: 80, color: '#1a1a2e' },
  { x: 8600,  y: GROUND_Y, w: 2800, h: 80, color: '#1a1a2e' },
  { x: 11400, y: GROUND_Y, w: 3200, h: 80, color: '#1a1a2e' },

  // ── Zone 1 · Intro ─────────────────────────────────────────────────────────
  {
    x: 400, y: 720, w: 180, h: 18,
    label: 'Hello World',
    infoId: 'z1-hello',
    tidbit: 'Welcome aboard. George builds scalable web & AI systems. Jump the towers to learn more.',
  },
  {
    x: 680, y: 640, w: 180, h: 18,
    label: 'SF Bay Area',
    infoId: 'z1-sf',
    tidbit: 'Based in San Francisco. 5+ years shipping fintech, crypto, and education products.',
  },
  {
    x: 960, y: 560, w: 220, h: 18,
    label: 'Full-Stack',
    infoId: 'z1-fs',
    tidbit: 'Backend-heavy full-stack: Spring Boot, Rails, Django, Node, plus React when the UI needs love.',
  },
  {
    x: 1280, y: 620, w: 180, h: 18,
    label: 'AI Builder',
    infoId: 'z1-ai',
    tidbit: 'Ships LLM & agentic features end-to-end: RAG, Whisper pipelines, voice agents, prompt craft.',
  },
  {
    x: 1550, y: 700, w: 180, h: 18,
    label: 'Music + Code',
    infoId: 'z1-music',
    tidbit: 'Also a music producer. Built labsesh.io: a browser DAW, DJ decks, and live jam rooms.',
  },

  { x: 1900,  y: 700, w: 200, h: 18 },
  { x: 2200,  y: 650, w: 200, h: 18, moving: true, moveAxis: 'x', moveRange: 100, moveSpeed: 60 },

  // ── Zone 2 · Skills (priority stack featured) ──────────────────────────────
  {
    x: 2900, y: 680, w: 220, h: 18, color: NEON2, label: 'Java / Kotlin',
    infoId: 'sk-java',
    tidbit: 'Daily drivers at Prosper. Built & documented Spring Boot microservices across 10+ services.',
  },
  {
    x: 3200, y: 600, w: 220, h: 18, color: NEON2, label: 'Spring Boot',
    infoId: 'sk-spring',
    tidbit: 'Loan funnels, credit decisioning, verification workflows for 1M+ active borrowers.',
  },
  {
    x: 3500, y: 520, w: 220, h: 18, color: NEON2, label: 'Python',
    infoId: 'sk-python',
    tidbit: 'RAG tools, Whisper/HuggingFace pipelines, agentic workflows, and internal AI at Mirantis.',
  },
  {
    x: 3800, y: 450, w: 220, h: 18, color: NEON2, label: 'Ruby on Rails',
    infoId: 'sk-rails',
    tidbit: 'Migrated a legacy Rails email platform from SMTP → Iterable with zero downtime.',
  },
  {
    x: 4050, y: 540, w: 200, h: 18, color: NEON2, label: 'System Design',
    infoId: 'sk-sys',
    tidbit: 'Event-driven Pub/Sub, retries, HA, caching, partitioning. Designs that survive millions of events/day.',
  },
  {
    x: 4300, y: 640, w: 200, h: 18, color: NEON2, label: 'TypeScript',
    infoId: 'sk-ts',
    tidbit: 'Next.js + Firebase crypto dashboards, typed APIs, and this very portfolio platformer.',
  },
  {
    x: 4550, y: 720, w: 200, h: 18, color: NEON2, label: 'React / Next.js',
    infoId: 'sk-react',
    tidbit: 'Production UIs from loan apps to subscription pricing A/B tests (+20% conversion at Udemy).',
    moving: true, moveAxis: 'x', moveRange: 120, moveSpeed: 55,
  },
  {
    x: 4820, y: 660, w: 220, h: 18, color: NEON2, label: 'AWS / GCP',
    infoId: 'sk-cloud',
    tidbit: 'GCS migrations (8M docs), BigQuery, Docker/K8s, CI/CD. Cloud is home base.',
  },
  {
    x: 5100, y: 580, w: 220, h: 18, color: NEON2, label: 'LLMs / RAG',
    infoId: 'sk-llm',
    tidbit: 'Internal knowledge tools for ~150 employees. LangChain, vector search, agent loops.',
  },

  // Jetpack perch: high & awkward to reach
  {
    x: 3980, y: 220, w: 140, h: 16, color: '#ffe066', label: '???',
    infoId: 'jetpack-hint',
    tidbit: 'Something powerful was stashed up here…',
    hasJetpack: true,
  },
  // stepping stones toward jetpack
  { x: 3700, y: 360, w: 100, h: 14, color: '#ffe066' },
  { x: 3850, y: 290, w: 100, h: 14, color: '#ffe066', moving: true, moveAxis: 'y', moveRange: 50, moveSpeed: 40 },

  // ── Zone 3 · Projects ──────────────────────────────────────────────────────
  {
    x: 5900, y: 680, w: 340, h: 22, color: '#1e2a1e', label: 'DBSurfer',
    infoId: 'pr-dbsurfer',
    tidbit: 'Latest build: browser-based DB client for Postgres, MySQL, SQL Server, SQLite, Mongo, Redis. AI assist included. Open source on GitHub.',
  },
  {
    x: 6380, y: 580, w: 340, h: 22, color: '#1e2a1e', label: 'LabSesh',
    infoId: 'pr-labsesh',
    tidbit: 'Live at labsesh.io: collaborative browser DAW, pro DJ decks, and 4-player WebRTC jam rooms.',
  },
  {
    x: 6860, y: 680, w: 340, h: 22, color: '#1e2a1e', label: 'AlphaLoom',
    infoId: 'pr-alpha',
    tidbit: 'Solo-built AI trading platform: signals, agents, and live market dashboards.',
  },
  {
    x: 7340, y: 580, w: 340, h: 22, color: '#1e2a1e', label: 'Meshenger',
    infoId: 'pr-mesh',
    tidbit: 'Bluetooth offline messaging between any device. Flutter / Dart mesh chat.',
  },
  // Bonus perch above the station row
  {
    x: 7150, y: 460, w: 200, h: 18, color: '#1e2a1e', label: 'streamcall.ai',
    infoId: 'pr-stream',
    tidbit: 'AI voice agents that dial CRMs: inbound & outbound sales calls with LLMs + Twilio.',
  },
  {
    x: 7820, y: 680, w: 340, h: 22, color: '#1e2a1e', label: 'Deadzone',
    infoId: 'pr-dead',
    tidbit: 'Online multiplayer survival with sockets + Three.js. Don\'t get eaten.',
  },
  {
    x: 8200, y: 680, w: 200, h: 18,
    label: 'Rhythm Wireless',
    infoId: 'pr-rhythm',
    tidbit: 'Helium hotspot deploy + crypto payments. Low-level Bluetooth device comms in the field.',
  },
  { x: 8500, y: 720, w: 200, h: 18, moving: true, moveAxis: 'y', moveRange: 80, moveSpeed: 45 },

  // ── Zone 4 · Experience – ascending timeline ────────────────────────────────
  {
    x: 8700, y: 740, w: 280, h: 18, color: '#1a1e2e', label: 'SFSU · CS + TA',
    infoId: 'ex-sfsu',
    tidbit: 'B.S. Computer Science (2021). Tutored data structures & algorithms; led labs & code reviews.',
  },
  {
    x: 9050, y: 660, w: 280, h: 18, color: '#1a1e2e', label: 'Prosper · SE I',
    infoId: 'ex-p1',
    tidbit: 'Helped launch the Prosper Credit Card: frontend + bureau microservices. Millions in new ARR.',
  },
  {
    x: 9400, y: 580, w: 280, h: 18, color: '#1a1e2e', label: 'Prosper · SE II',
    infoId: 'ex-p2',
    tidbit: 'Plaid upgrades, payment services, and cross-team Spring Boot delivery across 10+ services.',
  },
  {
    x: 9750, y: 500, w: 280, h: 18, color: '#1a1e2e', label: 'Udemy · SE II',
    infoId: 'ex-udemy',
    tidbit: 'Subscriptions pricing (+20% conversion). Transcoding pipelines for 100k+ hours → 220M learners.',
  },
  {
    x: 10100, y: 420, w: 280, h: 18, color: '#1a1e2e', label: 'Mirantis · AI',
    infoId: 'ex-mir',
    tidbit: 'RAG knowledge tools, Whisper meeting summarization. ~10 hrs/week saved for teams.',
  },
  {
    x: 10450, y: 340, w: 280, h: 18, color: '#1a1e2e', label: 'Rhythm · Lead',
    infoId: 'ex-rw',
    tidbit: 'Sole engineer: Next.js/Vercel/Firebase crypto analytics across ~5k wallets. Mentored an intern.',
  },
  {
    x: 10800, y: 420, w: 280, h: 18, color: '#1a1e2e', label: 'Prosper · SE III',
    infoId: 'ex-p3',
    tidbit: 'LLM features on personal loans, 8M-doc GCS migration, Pub/Sub at millions of events/day.',
  },
  { x: 11100, y: 560, w: 200, h: 18 },
  { x: 11300, y: 680, w: 200, h: 18 },

  // ── Zone 5 · Contact / End flag ────────────────────────────────────────────
  {
    x: 11500, y: 680, w: 200, h: 18, color: NEON,
    label: 'GitHub',
    infoId: 'ct-gh',
    tidbit: 'github.com/george-freedland: open source, experiments, and this game.',
  },
  {
    x: 11800, y: 580, w: 200, h: 18, color: NEON,
    label: 'LinkedIn',
    infoId: 'ct-li',
    tidbit: 'linkedin.com/in/george-freedland. Say hi if you want to build something ambitious.',
  },
  {
    x: 12100, y: 680, w: 200, h: 18, color: NEON,
    label: 'Email',
    infoId: 'ct-em',
    tidbit: 'george.freedland@gmail.com. Always open to interesting problems.',
  },
  {
    x: 12400, y: 580, w: 200, h: 18, color: NEON,
    label: 'Languages',
    infoId: 'ct-lang',
    tidbit: 'Python · TypeScript · Java · Kotlin · Ruby · Go · Rust · C/C++ · SQL. Pick the right tool.',
  },
  { x: 12700, y: 720, w: 1000, h: 22, color: '#0d1f0d' },
];

// ═════════════════════════════════════════════════════════════════════════════
// SPIKES  (instant kill)
// ═════════════════════════════════════════════════════════════════════════════
export const SPIKES: Spike[] = [
  { x: 420,   y: GROUND_Y, w: 160 }, // under first platform gap
  { x: 1820,  y: GROUND_Y, w: 80 },
  { x: 4400,  y: GROUND_Y, w: 80 },
  { x: 8400,  y: GROUND_Y, w: 80 },
  { x: 11200, y: GROUND_Y, w: 80 },
];

// ═════════════════════════════════════════════════════════════════════════════
// ENEMIES
// ═════════════════════════════════════════════════════════════════════════════
export const ENEMIES: Enemy[] = [
  // Ground patrols
  { id: 0,  x: 700,   y: GROUND_Y - 40, w: 32, h: 40, patrolLeft: 620,  patrolRight: 1100, speed: 85  },
  { id: 1,  x: 1100,  y: GROUND_Y - 40, w: 32, h: 40, patrolLeft: 900,  patrolRight: 1700, speed: 80  },
  { id: 2,  x: 1500,  y: GROUND_Y - 40, w: 32, h: 40, patrolLeft: 1300, patrolRight: 1800, speed: 95  },
  { id: 3,  x: 2100,  y: GROUND_Y - 40, w: 32, h: 40, patrolLeft: 2050, patrolRight: 2500, speed: 90  },
  { id: 4,  x: 3100,  y: GROUND_Y - 40, w: 32, h: 40, patrolLeft: 2900, patrolRight: 3600, speed: 88  },
  { id: 5,  x: 3700,  y: GROUND_Y - 40, w: 32, h: 40, patrolLeft: 3400, patrolRight: 4100, speed: 100 },
  { id: 6,  x: 4800,  y: GROUND_Y - 40, w: 32, h: 40, patrolLeft: 4600, patrolRight: 5400, speed: 100 },
  { id: 7,  x: 5200,  y: GROUND_Y - 40, w: 32, h: 40, patrolLeft: 5000, patrolRight: 5700, speed: 105 },
  { id: 8,  x: 6200,  y: GROUND_Y - 40, w: 32, h: 40, patrolLeft: 5900, patrolRight: 7000, speed: 100 },
  { id: 9,  x: 6800,  y: GROUND_Y - 40, w: 32, h: 40, patrolLeft: 6500, patrolRight: 7400, speed: 95  },
  { id: 10, x: 7200,  y: GROUND_Y - 40, w: 32, h: 40, patrolLeft: 6900, patrolRight: 8000, speed: 110 },
  { id: 11, x: 7800,  y: GROUND_Y - 40, w: 32, h: 40, patrolLeft: 7500, patrolRight: 8300, speed: 100 },
  { id: 12, x: 9000,  y: GROUND_Y - 40, w: 32, h: 40, patrolLeft: 8700, patrolRight: 9500, speed: 105 },
  { id: 13, x: 9600,  y: GROUND_Y - 40, w: 32, h: 40, patrolLeft: 9200, patrolRight:10200, speed: 110 },
  { id: 14, x: 10400, y: GROUND_Y - 40, w: 32, h: 40, patrolLeft:10000, patrolRight:11000, speed: 105 },
  { id: 15, x: 11600, y: GROUND_Y - 40, w: 32, h: 40, patrolLeft:11400, patrolRight:12200, speed: 95  },
  { id: 16, x: 12200, y: GROUND_Y - 40, w: 32, h: 40, patrolLeft:11800, patrolRight:12800, speed: 100 },
  { id: 17, x: 12600, y: GROUND_Y - 40, w: 32, h: 40, patrolLeft:12400, patrolRight:13200, speed: 90  },

  // Flying drones
  { id: 20, x: 500,  y: 520, w: 36, h: 28, patrolLeft: 350,  patrolRight: 900,  speed: 65,
    type: 'flying', patrolTop: 440, patrolBottom: 600 },
  { id: 21, x: 800,  y: 480, w: 36, h: 28, patrolLeft: 600,  patrolRight: 1400, speed: 70,
    type: 'flying', patrolTop: 400, patrolBottom: 560 },
  { id: 22, x: 1600, y: 450, w: 36, h: 28, patrolLeft: 1400, patrolRight: 2000, speed: 80,
    type: 'flying', patrolTop: 360, patrolBottom: 540 },
  { id: 23, x: 3100, y: 400, w: 36, h: 28, patrolLeft: 2900, patrolRight: 3800, speed: 85,
    type: 'flying', patrolTop: 320, patrolBottom: 480 },
  { id: 24, x: 3600, y: 340, w: 36, h: 28, patrolLeft: 3400, patrolRight: 4200, speed: 90,
    type: 'flying', patrolTop: 260, patrolBottom: 420 },
  { id: 25, x: 4100, y: 300, w: 36, h: 28, patrolLeft: 3900, patrolRight: 4600, speed: 75,
    type: 'flying', patrolTop: 240, patrolBottom: 380 },
  { id: 26, x: 5000, y: 380, w: 36, h: 28, patrolLeft: 4800, patrolRight: 5600, speed: 85,
    type: 'flying', patrolTop: 300, patrolBottom: 460 },
  { id: 27, x: 6500, y: 420, w: 36, h: 28, patrolLeft: 6000, patrolRight: 7600, speed: 90,
    type: 'flying', patrolTop: 340, patrolBottom: 520 },
  { id: 28, x: 7100, y: 360, w: 36, h: 28, patrolLeft: 6800, patrolRight: 7800, speed: 95,
    type: 'flying', patrolTop: 280, patrolBottom: 440 },
  { id: 29, x: 7800, y: 380, w: 36, h: 28, patrolLeft: 7400, patrolRight: 8400, speed: 80,
    type: 'flying', patrolTop: 300, patrolBottom: 460 },
  { id: 30, x: 9000, y: 320, w: 36, h: 28, patrolLeft: 8700, patrolRight: 9600, speed: 88,
    type: 'flying', patrolTop: 240, patrolBottom: 400 },
  { id: 31, x: 9600, y: 280, w: 36, h: 28, patrolLeft: 9200, patrolRight:10600, speed: 95,
    type: 'flying', patrolTop: 200, patrolBottom: 360 },
  { id: 32, x: 10800,y: 300, w: 36, h: 28, patrolLeft:10400, patrolRight:11200, speed: 90,
    type: 'flying', patrolTop: 220, patrolBottom: 380 },
  { id: 33, x: 11900,y: 400, w: 36, h: 28, patrolLeft:11500, patrolRight:12800, speed: 85,
    type: 'flying', patrolTop: 320, patrolBottom: 500 },
  { id: 34, x: 12400,y: 350, w: 36, h: 28, patrolLeft:12100, patrolRight:13200, speed: 100,
    type: 'flying', patrolTop: 260, patrolBottom: 440 },
];

// ═════════════════════════════════════════════════════════════════════════════
// ZONE TOASTS (shown when crossing trigger; do NOT pause the game)
// ═════════════════════════════════════════════════════════════════════════════
export const ZONE_CARDS: ZoneCard[] = [
  {
    id: 1,
    triggerX: 200,
    triggerW: 180,
    title: '> ZONE 1 · INTRO',
    lines: [
      'George Freedland · Software Engineer',
      'Talk to the old astronaut (E) for a briefing.',
    ],
  },
  {
    id: 2,
    triggerX: 2750,
    triggerW: 200,
    title: '> SKILLS.exe',
    lines: [
      'Java · Kotlin · Spring Boot · Python · Rails',
      'Land on a glowing platform for a tidbit.',
    ],
  },
  {
    id: 3,
    triggerX: 5750,
    triggerW: 200,
    title: '> PROJECTS',
    lines: [
      'DBSurfer · LabSesh · AlphaLoom · more…',
      'Stand on a station to peek at the build.',
    ],
  },
  {
    id: 4,
    triggerX: 8550,
    triggerW: 200,
    title: '> EXPERIENCE',
    lines: [
      'SFSU → Prosper → Udemy → Mirantis → Lead',
      'Climb the timeline. Each ledge is a chapter.',
    ],
  },
  {
    id: 5,
    triggerX: 11350,
    triggerW: 200,
    title: '> CONTACT',
    lines: [
      'You made it to the far station.',
      'Grab a link platform or race to FINISH.',
    ],
  },
];

// ═════════════════════════════════════════════════════════════════════════════
// OLD ASTRONAUT (start of level)
// ═════════════════════════════════════════════════════════════════════════════
export const ASTRONAUT: Npc = {
  x: 220,
  y: GROUND_Y - 56,
  w: 36,
  h: 56,
  interactR: 90,
  lines: [
    {
      title: '> OLD ASTRONAUT',
      body: [
        'Welcome to georgefreedland.com,',
        'a playable tour of George\'s work.',
      ],
    },
    {
      title: '> THE MISSION',
      body: [
        'Explore zones: Skills → Projects → Experience.',
        'Land on platforms for tidbits. Survive the drones.',
      ],
    },
    {
      title: '> JETPACK CLUE',
      body: [
        'A thruster pack waits on a high gold ledge',
        'above the skill towers. Climb, then press E to fly.',
      ],
    },
  ],
};

// ═════════════════════════════════════════════════════════════════════════════
// CHECKPOINTS  (respawn here on death)
// ═════════════════════════════════════════════════════════════════════════════
export const CHECKPOINTS: Checkpoint[] = [
  { x: 100,   zoneId: 1 },
  { x: 2800,  zoneId: 2 },
  { x: 5800,  zoneId: 3 },
  { x: 8600,  zoneId: 4 },
  { x: 11400, zoneId: 5 },
];

// ═════════════════════════════════════════════════════════════════════════════
// END FLAG position
// ═════════════════════════════════════════════════════════════════════════════
export const END_FLAG_X = 13400;
export const END_FLAG_Y = GROUND_Y - 160;

/** World XY of the jetpack pickup (derived from platform with hasJetpack). */
export function getJetpackPos(): { x: number; y: number } {
  const p = PLATFORMS.find(pl => pl.hasJetpack);
  if (!p) return { x: 3980, y: 180 };
  return { x: p.x + p.w / 2 - 14, y: p.y - 36 };
}
